// The customer book: who the bank actually banks.
//
// This listed every row in kyc.customers, which meant half-finished
// registrations and abandoned test sign-ups sat in the same table as real
// customers — a hundred rows of noise, no filters, no pagination, and every
// field masked so the one column a reviewer scans by (the name) was a row of
// asterisks. A customer is someone onboarding accepted, so that is the default
// here; the other states are available deliberately rather than by accident.

import {
  Alert,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { customersApi, type CustomerListRow } from '../../api/customers.api.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { APP_LOCALE } from '../../utils/locale.js';

/** Ten rows is one screen a reviewer can actually work through. */
const PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Customers (approved)' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  { value: 'PENDING_KYC', label: 'Pending KYC' },
  { value: 'PENDING_EDD', label: 'Pending EDD' },
  { value: 'PROHIBITED', label: 'Prohibited' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'ALL', label: 'Everyone (incl. unfinished)' },
];

export function CustomerListPage(): JSX.Element {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [accountType, setAccountType] = useState('');
  const [country, setCountry] = useState('');
  const [sort, setSort] = useState<'created' | 'updated' | 'name'>('created');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Changing what is asked for starts again at page 1; staying on page 4 of a
  // filter that now has one page shows an empty table.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, accountType, country, sort, direction]);

  const filters = useMemo(
    () => ({
      ...(debouncedSearch !== '' ? { search: debouncedSearch } : {}),
      status,
      ...(accountType !== '' ? { accountType } : {}),
      ...(country.trim() !== '' ? { country: country.trim() } : {}),
      sort,
      direction,
      page,
      limit: PAGE_SIZE,
    }),
    [debouncedSearch, status, accountType, country, sort, direction, page],
  );

  const { data, isFetching, error } = useQuery({
    queryKey: ['customers', filters],
    queryFn: async () => customersApi.list(filters),
    placeholderData: keepPreviousData,
  });

  const rows: CustomerListRow[] = data?.entries ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const toggleSort = (column: 'created' | 'updated' | 'name'): void => {
    if (sort === column) setDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSort(column);
      setDirection(column === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Customers
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Search name, e-mail, phone or customer ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280, flexGrow: 1 }}
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Type"
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PERSONAL">PERSONAL</MenuItem>
            <MenuItem value="BUSINESS">BUSINESS</MenuItem>
          </TextField>
          <TextField
            label="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            sx={{ minWidth: 120 }}
          />
        </Stack>
      </Paper>

      {error !== null && error !== undefined ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load customers: {error instanceof Error ? error.message : 'unknown error'}
        </Alert>
      ) : null}

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sort === 'name' ? direction : false}>
                <TableSortLabel
                  active={sort === 'name'}
                  direction={sort === 'name' ? direction : 'asc'}
                  onClick={() => toggleSort('name')}
                >
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>KYC</TableCell>
              <TableCell>Risk</TableCell>
              <TableCell sortDirection={sort === 'created' ? direction : false}>
                <TableSortLabel
                  active={sort === 'created'}
                  direction={sort === 'created' ? direction : 'desc'}
                  onClick={() => toggleSort('created')}
                >
                  Registered
                </TableSortLabel>
              </TableCell>
              <TableCell>Approved</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" py={2}>
                    {status === 'ACTIVE' && debouncedSearch === ''
                      ? 'No approved customers yet — applications in progress are in Onboarding.'
                      : 'No customer matches these filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.customerId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => void navigate(`/customers/${row.customerId}`)}
                >
                  <TableCell>
                    <Typography>{row.name === '' ? row.email : row.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.name === '' ? row.customerId : row.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.accountType === '—' ? (
                      '—'
                    ) : (
                      <Chip label={row.accountType} size="small" />
                    )}
                  </TableCell>
                  <TableCell>{row.country}</TableCell>
                  <TableCell>{row.phone}</TableCell>
                  <TableCell>
                    <StatusBadge kind="kyc" value={row.status} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge kind="risk" value={row.riskRating} />
                  </TableCell>
                  <TableCell>{new Date(row.createdAt).toLocaleDateString(APP_LOCALE)}</TableCell>
                  <TableCell>
                    {row.approvedAt === null
                      ? '—'
                      : new Date(row.approvedAt).toLocaleDateString(APP_LOCALE)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
          spacing={2}
        >
          <Typography variant="caption" color="text.secondary">
            {total === 0
              ? 'No customers'
              : `${String((page - 1) * PAGE_SIZE + 1)}-${String(
                  Math.min(page * PAGE_SIZE, total),
                )} of ${String(total)}`}
          </Typography>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, next: number) => setPage(next)}
            size="small"
            showFirstButton
            showLastButton
          />
        </Stack>
      </Paper>
    </>
  );
}
